import React, { useEffect, useState } from 'react';
import { Button, Drawer, Input, Space, Table, Tag, Typography, notification } from 'antd';
import { EyeOutlined, PaperClipOutlined, ReloadOutlined } from '@ant-design/icons';
import messageService from '../../services/messageService';

const AdminInbox = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    messageService.getInboxMessages({ page, limit: 10, search })
      .then((response) => {
        if (cancelled) return;
        setMessages(response.messages || []);
        setTotal(response.total || 0);
      })
      .catch((error) => {
        if (cancelled) return;
        notification.error({
          message: 'Failed to load inbox',
          description: error.message || 'Please try again later',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [page, search, refresh]);

  const columns = [
    {
      title: 'Client',
      key: 'sender',
      render: (_, message) => (
        <div>
          <div className="font-medium">{message.sender_name}</div>
          <div className="text-xs text-gray-500">{message.sender_email}</div>
        </div>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      render: (subject, message) => (
        <div>
          <div className="font-medium">
            {subject || 'No Subject'} {message.attachments?.length > 0 ? <PaperClipOutlined /> : null}
          </div>
          <div className="text-xs text-gray-500">
            {message.message?.length > 80 ? `${message.message.substring(0, 80)}...` : message.message}
          </div>
        </div>
      ),
    },
    {
      title: 'Received',
      dataIndex: 'created_at',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, message) => (
        <Button icon={<EyeOutlined />} onClick={() => setSelectedMessage(message)}>View</Button>
      ),
    },
  ];

  return (
    <div>
      <Typography.Paragraph type="secondary">
        Messages and replies from clients to the admin team.
      </Typography.Paragraph>
      <Space className="mb-4" wrap>
        <Input.Search
          placeholder="Search client or message"
          allowClear
          onSearch={(value) => { setSearch(value.trim()); setPage(1); }}
          style={{ width: 280, maxWidth: '100%' }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={messages}
        loading={loading}
        scroll={{ x: 640 }}
        pagination={{ current: page, pageSize: 10, total, onChange: setPage, showSizeChanger: false }}
      />
      <Drawer
        title={selectedMessage?.subject || 'Message Details'}
        open={Boolean(selectedMessage)}
        onClose={() => setSelectedMessage(null)}
        width={600}
      >
        {selectedMessage ? (
          <div>
            <Typography.Paragraph>
              <strong>{selectedMessage.sender_name}</strong><br />
              {selectedMessage.sender_email}<br />
              {selectedMessage.created_at ? new Date(selectedMessage.created_at).toLocaleString() : 'N/A'}
            </Typography.Paragraph>
            {selectedMessage.importance === 'high' ? <Tag color="red">Important</Tag> : null}
            <Typography.Paragraph className="whitespace-pre-line">{selectedMessage.message}</Typography.Paragraph>
            {selectedMessage.attachments?.length > 0 ? (
              <Space direction="vertical">
                <Typography.Text strong>Attachments</Typography.Text>
                {selectedMessage.attachments.map((attachment) => (
                  <a key={attachment.id} href={attachment.url} target="_blank" rel="noopener noreferrer">
                    <PaperClipOutlined /> {attachment.name}
                  </a>
                ))}
              </Space>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default AdminInbox;
